import { PlusIcon } from '@phosphor-icons/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCreateDormakabaKey } from '@/api/keyless/dormakaba/create-dormakaba-key'
import { useGetDormakabaCommonAreas } from '@/api/keyless/dormakaba/get-dormakaba-access-points'
import { getDormakabaActivationErrorMessage } from '@/api/keyless/dormakaba/get-dormakaba-activation-error-message'
import {
  getDormakabaIsConnectedQueryKey,
  useGetDormakabaIsConnected,
} from '@/api/keyless/dormakaba/get-dormakaba-is-connected'
import { useUpdateDormakabaUser } from '@/api/keyless/dormakaba/update-dormakaba-user'
import { useIsDormakabaBuildingConnected } from '@/api/property-keyless/dormakaba/use-is-dormakaba-building-connected'
import { Button } from '@/components/ui/button'
import { ChipSelect } from '@/components/ui/chip-select'
import { Flex } from '@/components/ui/flex'
import { Icon } from '@/components/ui/icon'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { toastFactory } from '@/factories'
import { resetQueries } from '@/utils'
import { getApiErrorMessage } from '@/utils/error'

import * as S from '../keyless-access.styled'
import { KeylessBuildingNotConnected } from '../keyless-access-building-not-connected'
import { KeylessTabSkeleton } from '../keyless-access-tab-skeleton'
import { DormakabaTabProps } from './dormakaba-tab.types'

export const DormakabaTab = ({
  buildingId,
  clientApartment,
  clientEmail,
  clientId,
  onConnectionChange,
  onLoadingChange,
}: DormakabaTabProps) => {
  const { t } = useTranslation(['clients'])
  const {
    isBuildingConnected,
    isLiveProbeUnavailable,
    isLoading: isLoadingBuildingConnection,
  } = useIsDormakabaBuildingConnected(buildingId, { withLiveProbe: true })

  useEffect(() => {
    onConnectionChange?.(false)
  }, [onConnectionChange])

  useEffect(() => {
    onLoadingChange?.(isLoadingBuildingConnection)
    return () => onLoadingChange?.(false)
  }, [isLoadingBuildingConnection, onLoadingChange])

  const [selectedCommonAreaIds, setSelectedCommonAreaIds] = useState<string[]>([])

  const { mutateAsync: createKey, isPending: isActivating } = useCreateDormakabaKey()
  const { mutateAsync: updateUser, isPending: isUpdating } = useUpdateDormakabaUser()
  const { data: userConnection, isLoading: isLoadingUserConnection } = useGetDormakabaIsConnected({
    email: clientEmail,
  })
  const isUserConnected = userConnection?.isConnected === true
  const isSaving = isActivating || isUpdating
  const {
    data: commonAreas = [],
    isError: isCommonAreasError,
    isLoading: isLoadingCommonAreas,
  } = useGetDormakabaCommonAreas({ buildingId }, { enabled: isBuildingConnected })

  const commonAreaOptions = commonAreas.map((a) => ({
    label: a.name,
    value: String(a.commonAreaId),
  }))

  const activate = async () => {
    const commonAreaIds = commonAreas
      .filter((a) => selectedCommonAreaIds.includes(String(a.commonAreaId)))
      .map((a) => a.commonAreaId)
    await createKey({
      clientId,
      commonAreaIds: commonAreaIds.length > 0 ? commonAreaIds : undefined,
    })
    await resetQueries(getDormakabaIsConnectedQueryKey({ email: clientEmail }))
    toastFactory.success(t('clients:keylessAccess.dormakaba.toasts.activateSuccess'))
  }

  const update = async () => {
    await updateUser({
      commonAreas: selectedCommonAreaIds.map((commonAreaID) => ({ commonAreaID })),
      email: clientEmail,
    })
    toastFactory.success(t('clients:keylessAccess.dormakaba.toasts.updateSuccess'))
  }

  const handleSubmit = async () => {
    try {
      await (isUserConnected ? update() : activate())
      setSelectedCommonAreaIds([])
    } catch (error) {
      toastFactory.error(getDormakabaActivationErrorMessage(error, getApiErrorMessage(error)))
    }
  }

  if (isLoadingBuildingConnection) return <KeylessTabSkeleton rowCount={1} />

  if (!isBuildingConnected) {
    return (
      <KeylessBuildingNotConnected
        title={t('clients:keylessAccess.dormakaba.buildingNotConnected.title')}
        message={t(
          isLiveProbeUnavailable
            ? 'clients:keylessAccess.dormakaba.errors.serviceUnavailable'
            : 'clients:keylessAccess.dormakaba.buildingNotConnected.message',
        )}
      />
    )
  }

  return (
    <S.TabContent>
      <S.FieldRow>
        <Flex gap={6} align="center">
          <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
            {t('clients:keylessAccess.tabLabels.dormakaba')}
          </Typography>
        </Flex>
        <Flex align="center" padding="0 0 0 10">
          <Typography size="text-small" color="black800">
            {clientApartment ?? EMPTY_VALUE}
          </Typography>
        </Flex>
      </S.FieldRow>
      <S.FieldRow>
        <Flex gap={6} align="center">
          <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
            {t('clients:keylessAccess.dormakaba.commonAreas.label')}
          </Typography>
        </Flex>
        <ChipSelect
          size="small"
          options={commonAreaOptions}
          value={selectedCommonAreaIds}
          placeholder={t('clients:keylessAccess.dormakaba.commonAreas.placeholder')}
          loading={isLoadingCommonAreas}
          disabled={isSaving}
          error={
            isCommonAreasError
              ? t('clients:keylessAccess.dormakaba.commonAreas.loadError')
              : undefined
          }
          onChange={setSelectedCommonAreaIds}
        />
      </S.FieldRow>
      <S.ActionRow>
        <Button
          size="small"
          loading={isSaving}
          disabled={isLoadingCommonAreas || isLoadingUserConnection}
          onClick={() => void handleSubmit()}
        >
          {isUserConnected ? (
            t('clients:keylessAccess.dormakaba.update')
          ) : (
            <>
              {t('clients:keylessAccess.dormakaba.activate')}
              <Icon icon={PlusIcon} size="1.6rem" />
            </>
          )}
        </Button>
      </S.ActionRow>
    </S.TabContent>
  )
}
